import React, { Fragment } from "react"
import { navigate } from "gatsby"
import { Flex, Box, Container } from "theme-ui"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import Layout from "templates/layout"
import ContentBox from "components/content"
import styled from "@emotion/styled"
import Breadcrums from "components/breadcrums"
import TextWithImage from "components/pages/textWithImage"
import SingleColumn from "components/pages/singleColumn"
import MultiColumn from "components/pages/multiColumn"
import Gallery from "components/pages/gallery"
import Accordion from "components/pages/accordion"
import ExternalScript from "components/pages/externalScript"
import { useBreakpointIndex } from "@theme-ui/match-media"
import "assets/css/table.css"

const PageWrapper = styled.div`
    position: relative;
    color: #ffffff;
    & .caption-box-topbanner {
        text-shadow: rgb(0, 0, 0, 0.6) 2px 2px 5px;
    }
    & h2.header-title {
        border-left: ${(props) => props.theme.colors.primary} double;
        border-width: 4px 4px 0 4px;
        box-shadow: -3px 0 0 0 ${(props) => props.theme.colors.primary};
        padding-left: 0.5rem;
        line-height: 0.75;
        margin-left: -1.2rem;
        font-weight: normal;
        margin-bottom: 1rem;
    }

    & .show-md {
        display: none;
    }
    & .hide-md {
        display: block;
    }
    & figure.table {
        margin: 0;
        overflow-x: auto;
        width: 100% !important;
    }
    @media screen and (max-width: ${(props) => props.theme.breakpoints[8]}) {
        & table {
            table-layout: auto;
        }
    }

    @media screen and (max-width: ${(props) => props.theme.breakpoints[4]}) {
        & .show-md {
            display: block;
        }
        & .hide-md {
            display: none;
        }
        & .grid-content {
            grid-template-columns: 1fr;
        }
    }
`

const FullWidth = styled.div`
    margin-left: calc(50% - 50vw) !important;
    margin-right: calc(50% - 50vw) !important;
    background: ${(props) => props.theme.colors.soft_purple};

    & .no-banner {
        padding: 1rem;
        margin-bottom: 3rem;
    }
`

const PageTitle = styled.h1`
    color: ${(props) => props.theme.colors.primary};
    margin-bottom: 1rem;
`
// onlyCustomType
const onlyCustomType = {
    partial: "partial", // include custom element to existing layout
    full: "full", // replace the layout with custom component
}
// markup
const IndexPage = (props) => {
    const breakpoint = useBreakpointIndex()
    const {
        location,
        data,
        bgColor,
        galleryMinWidth,
        breadcrums,
        parentLink,
        customElement,
        onlyCustom,
    } = props

    if (data === undefined) {
        return navigate("/")
    }

    const { pageData } = data
    const category = pageData.category
    const contents = pageData.content

    const breadcrumbTitle = category.subtitle || category.name

    const linksBreadcrums = breadcrums || [breadcrumbTitle, pageData.title]

    let descriptionPage = (
        <Flex sx={{ marginBottom: "1rem" }}>
            <div
                dangerouslySetInnerHTML={{
                    __html: pageData.description,
                }}
            ></div>
        </Flex>
    )

    let pageContent = null
    if (contents !== undefined && contents) {
        pageContent = contents.map((content, index) => {
            let html = null
            if (
                content.Single_Column !== undefined &&
                content.Single_Column !== null
            ) {
                html = <SingleColumn data={content.Single_Column} key={index} />
            } else if (
                content.Text_With_Image !== undefined &&
                content.Text_With_Image !== null
            ) {
                let doubleText = false
                let desc = null
                if (
                    index === 0 &&
                    content.Text_With_Image.Layout_Type !== "Full_Width_Image"
                ) {
                    doubleText = true
                    desc = descriptionPage
                    descriptionPage = null
                }
                html = (
                    <Fragment key={index}>
                        <TextWithImage
                            data={content.Text_With_Image}
                            description={pageData.description}
                            doubleText={doubleText}
                        />
                    </Fragment>
                )
            } else if (
                content.Gallery_Image !== undefined &&
                content.Gallery_Image !== null
            ) {
                html = (
                    <Gallery
                        data={content.Gallery_Image}
                        minWidth={galleryMinWidth || 0}
                        key={index}
                    />
                )
            } else if (
                content.Multi_Columns !== undefined &&
                content.Multi_Columns !== null
            ) {
                html = <MultiColumn data={content.Multi_Columns} key={index} />
            } else if (
                content.Accordion !== undefined &&
                content.Accordion !== null
            ) {
                html = <Accordion data={content.Accordion} key={index} />
            } else if (
                content.external_script !== undefined &&
                content.external_script !== null
            ) {
                html = (
                    <ExternalScript
                        data={content.external_script}
                        key={index}
                    />
                )
            }
            return html
        })
    }

    let bannerContent = (
        <>
            <FullWidth>
                <Container className="no-banner">
                    <PageTitle>{pageData.title}</PageTitle>
                    <Breadcrums
                        links={linksBreadcrums}
                        parentLink={parentLink}
                    />
                    {descriptionPage}
                </Container>
            </FullWidth>
        </>
    )
    if (pageData.banner)
        bannerContent = (
            <FullWidth>
                <Container>
                    <Flex
                        className="banner-wrapper"
                        sx={{
                            flexWrap: "wrap",
                            justifyContent: "space-between",
                            marginBottom: "1rem",
                            px: "1rem",
                            paddingTop: breakpoint > 4 ? "2rem" : "0",
                        }}
                    >
                        <Box
                            sx={{
                                flex: "0 0 auto",
                                width: breakpoint > 4 ? "50%" : "100%",
                            }}
                        >
                            <PageTitle>{pageData.title}</PageTitle>
                            <Breadcrums
                                links={linksBreadcrums}
                                parentLink={parentLink}
                            />
                            {descriptionPage}
                        </Box>
                        <Box
                            sx={{
                                flex: "0 0 auto",
                                width: breakpoint > 4 ? "40%" : "100%",
                            }}
                        >
                            <GatsbyImage
                                image={getImage(pageData.banner)}
                                alt={pageData.title}
                            />
                        </Box>
                    </Flex>
                </Container>
            </FullWidth>
        )

    let mainContent = (
        <>
            {bannerContent}
            {pageContent}
            {customElement || null}
        </>
    )

    if (onlyCustom === onlyCustomType.partial) {
        mainContent = (
            <>
                {bannerContent}
                {customElement}
            </>
        )
    }

    return onlyCustom === onlyCustomType.full ? (
        customElement
    ) : (
        <Layout
            page={pageData}
            metadata={pageData.metadata}
            location={location}
        >
            <PageWrapper>
                {/* <TopBanner data={topBarData} /> */}
                <ContentBox>{mainContent}</ContentBox>
            </PageWrapper>
        </Layout>
    )
}

export default IndexPage
